import React from 'react';
import { CheckCircle2, XCircle, Bell } from 'lucide-react';
import type { ConversationEventPayload } from '../hooks/useAgentChat';

export interface EventCardProps {
  payload: ConversationEventPayload;
  timestamp: string;
  /** Whether this is inside the narrow drawer (compact sizing) or the full chat view. */
  compact?: boolean;
}

/**
 * Inline notification card for Celery-driven conversation events.
 *
 * Today only handles `research_job_update` (ResearchJob completion/failure);
 * structured so new event types can be added with a simple case.
 */
export const EventCard: React.FC<EventCardProps> = ({ payload, timestamp, compact }) => {
  const isFailure = payload.status === 'failed';
  const Icon = isFailure ? XCircle : payload.status === 'completed' ? CheckCircle2 : Bell;

  const tone = isFailure
    ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-200'
    : 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-200';

  const iconTone = isFailure ? 'text-red-500' : 'text-emerald-600 dark:text-emerald-400';

  const title = payload.kind_display || 'Update';
  const summary = payload.summary || payload.status_display || '';
  const when = formatTimestamp(timestamp);

  return (
    <div
      className={[
        'flex items-start gap-2 rounded-lg border',
        tone,
        compact ? 'px-3 py-2' : 'px-4 py-3',
      ].join(' ')}
    >
      <Icon className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} flex-shrink-0 mt-0.5 ${iconTone}`} />
      <div className="min-w-0 flex-1">
        <div className={`flex items-baseline justify-between gap-2 ${compact ? 'text-xs' : 'text-sm'}`}>
          <span className="font-medium">{title}</span>
          {when && (
            <span className="text-[10px] opacity-60 flex-shrink-0">{when}</span>
          )}
        </div>
        {summary && (
          <div className={compact ? 'text-[11px] mt-0.5 opacity-80' : 'text-xs mt-1 opacity-80'}>
            {summary}
          </div>
        )}
      </div>
    </div>
  );
};

function formatTimestamp(ts: string): string {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

export default EventCard;
